import { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const ScrollablePillBar = ({ children, className = '' }) => {
  const { dir } = useLanguage();
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const absLeft = Math.abs(scrollLeft);
    setCanLeft(absLeft > 2);
    setCanRight(absLeft + clientWidth < scrollWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    el?.addEventListener('scroll', checkScroll, { passive: true });
    window.addEventListener('resize', checkScroll);
    return () => {
      el?.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  });

  const scroll = (direction) => {
    const delta = direction * 220 * (dir === 'rtl' ? -1 : 1);
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const PrevIcon = dir === 'rtl' ? ChevronRight : ChevronLeft;
  const NextIcon = dir === 'rtl' ? ChevronLeft : ChevronRight;

  const items = Array.isArray(children) ? children.flat() : [children];

  return (
    <div className={`flex items-center gap-2 ${className}`}>

      <motion.button
        onClick={() => scroll(-1)}
        animate={{ opacity: canLeft ? 0.8 : 0, pointerEvents: canLeft ? 'auto' : 'none' }}
        whileHover={{ opacity: 1, scale: 1.08 }}
        whileTap={{ scale: 0.93 }}
        transition={{ duration: 0.18 }}
        className="flex shrink-0 w-9 h-9 rounded-full items-center justify-center bg-emerald-600 text-white"
      >
        <PrevIcon size={14} />
      </motion.button>

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        className="flex items-center gap-5 overflow-x-auto no-scrollbar flex-1 py-2 px-1"
      >
        {items.map((child, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: i * 0.04, ease: 'easeOut' }}
            className="shrink-0"
          >
            {child}
          </motion.div>
        ))}
      </div>

      <motion.button
        onClick={() => scroll(1)}
        animate={{ opacity: canRight ? 0.8 : 0, pointerEvents: canRight ? 'auto' : 'none' }}
        whileHover={{ opacity: 1, scale: 1.08 }}
        whileTap={{ scale: 0.93 }}
        transition={{ duration: 0.18 }}
        className="flex shrink-0 w-9 h-9 rounded-full items-center justify-center bg-emerald-600 text-white"
      >
        <NextIcon size={14} />
      </motion.button>

    </div>
  );
};

export default ScrollablePillBar;
